import { z } from 'zod';

const trechoSchema = z
  .object({
    texto: z.string(),
    estilo: z.enum(['normal', 'enfase', 'italico', 'link']),
    href: z.string().optional(),
    marcas: z.array(z.enum(['negrito', 'italico', 'sublinhado', 'tachado'])).optional(),
  })
  .strict();

const alinhamentoSchema = z.enum(['esquerda', 'centro', 'direita', 'justificado']);

const blocoTextoSchema = z.discriminatedUnion('tipo', [
  z
    .object({
      id: z.string(),
      tipo: z.literal('subtitulo'),
      texto: z.string(),
      alinhamento: alinhamentoSchema.optional(),
    })
    .strict(),
  z
    .object({
      id: z.string(),
      tipo: z.literal('paragrafo'),
      trechos: z.array(trechoSchema),
      alinhamento: alinhamentoSchema.optional(),
    })
    .strict(),
  z
    .object({
      id: z.string(),
      tipo: z.literal('citacao'),
      trechos: z.array(trechoSchema),
      alinhamento: alinhamentoSchema.optional(),
    })
    .strict(),
  z
    .object({
      id: z.string(),
      tipo: z.literal('destaque'),
      paragrafos: z.array(z.array(trechoSchema)),
    })
    .strict(),
  z.object({ id: z.string(), tipo: z.literal('htmlBruto'), html: z.string() }).strict(),
]);

const capituloSchema = z
  .object({
    id: z.string(),
    titulo: z.string().nullable(),
    formatoTitulo: z
      .object({ nivel: z.union([z.literal(1), z.literal(2)]), alinhamento: alinhamentoSchema })
      .strict()
      .optional(),
    blocos: z.array(blocoTextoSchema),
  })
  .strict();

const parteSchema = z
  .object({
    id: z.string(),
    nome: z.string(),
    papelLabel: z.string(),
    papelDestacado: z.boolean(),
    contatos: z.array(z.string()),
    documento: z.string(),
  })
  .strict();

const grupoPartesSchema = z
  .object({
    id: z.string(),
    titulo: z.string(),
    destaque: z.enum(['brand', 'coral']),
    partes: z.array(parteSchema),
    observacao: z.string().nullable(),
  })
  .strict();

const identificacaoSchema = z
  .object({
    localSentenca: z.string(),
    instituicaoArbitral: z.string(),
    requerimentoNumero: z.string(),
    termoArbitragemId: z.string(),
    proferidaEm: z.string(),
    profissionalResponsavel: z.string(),
    plataformaUrl: z.string(),
    plataformaUrlExibicao: z.string(),
    autenticidadeUrl: z.string(),
    autenticidadeUrlExibicao: z.string(),
  })
  .strict();

const blocoResumoSchema = z
  .object({
    id: z.string(),
    icone: z.enum(['decisao', 'partes', 'proximosPassos']),
    titulo: z.string(),
    trechos: z.array(trechoSchema),
  })
  .strict();

const qualificacaoSchema = z
  .object({
    rotulo: z.string(),
    texto: z.string(),
    tom: z.enum(['informada', 'ausente']),
  })
  .strict();

const eventoLinhaTempoSchema = z
  .object({
    id: z.string(),
    ocorridoEm: z.string(),
    titulo: z.string(),
    descricao: z.string(),
    marco: z.enum(['etapa', 'conclusao']),
  })
  .strict();

const termoGlossarioSchema = z.object({ termo: z.string(), explicacao: z.string() }).strict();

const assinaturaSchema = z
  .object({
    nomeManuscrito: z.string(),
    nomeFormal: z.string(),
    cargo: z.string(),
    assinadoEm: z.string(),
  })
  .strict();

// rn11: as ÚNICAS chaves do institucional. `.strict()` é o teste-cerca de contrato — telefone,
// WhatsApp ou horário aqui reprovam o parse inteiro.
const instituicaoSchema = z
  .object({
    orgao: z.string(),
    logoUrl: z.string(),
    contatoEmail: z.string(),
    contatoSite: z.string(),
    assinaturaImagemUrl: z.string(),
    assinaturaCargo: z.string(),
    rodapeLegal: z.string(),
  })
  .strict();

const orientacaoDuvidaSchema = z.object({ titulo: z.string(), texto: z.string() }).strict();

export const sentencaArbitralDtoSchema = z
  .object({
    processoId: z.string(),
    numeroProcesso: z.string(),
    tipoDecisao: z.string(),
    proferidaEm: z.string(),
    codigoAutenticacao: z.string(),
    tituloTextoOficial: z.string(),
    identificacao: identificacaoSchema,
    gruposPartes: z.array(grupoPartesSchema),
    resumoDecisao: z.array(blocoResumoSchema),
    qualificacoes: z.array(qualificacaoSchema),
    capitulos: z.array(capituloSchema),
    assinatura: assinaturaSchema,
    linhaTempo: z.array(eventoLinhaTempoSchema),
    glossario: z.array(termoGlossarioSchema),
    orientacaoDuvida: orientacaoDuvidaSchema,
    instituicao: instituicaoSchema,
  })
  .strict();

export type SentencaArbitralDto = z.infer<typeof sentencaArbitralDtoSchema>;
export type TrechoSentencaDto = z.infer<typeof trechoSchema>;
